import { useCallback } from 'react';
import { useSweepApi } from '../sweep';
import { ObjectData } from '../../components/parser/ParserTypes';
import { BackendDocumentationMetadataTypesTab } from '../../components/documentation/tabs/types';
import {
  ConfigurationInstanceData,
  FieldMetadataRecordProperties,
  NameProperties,
} from '../../components/documentation/dependencies/DependenciesTypes';

export type NameSearchProperties = NameProperties & {
  metadataId: string;
};

export type ConfigurationMetadataRecordProperties = ConfigurationInstanceData & {
  objectName: string;
  type?: string;
};

type CartTypeAbstract = {
  type: 'field' | 'configuration' | 'object';
  metadata:
    | FieldMetadataRecordProperties
    | ConfigurationMetadataRecordProperties
    | ObjectSearchProperties;
};

interface CardTypeField extends CartTypeAbstract {
  type: 'field';
  metadata: FieldMetadataRecordProperties;
}

interface CardTypeConfiguration extends CartTypeAbstract {
  type: 'configuration';
  metadata: ConfigurationMetadataRecordProperties;
}

type ObjectSearchProperties = NameProperties & {
  metadataId: string;
  filename: string;
};

interface CardTypeName extends CartTypeAbstract {
  type: 'object';
  metadata: ObjectSearchProperties & { type: string };
}

export type CardType = CardTypeField | CardTypeConfiguration | CardTypeName;

export const useDocumentationApiFacade = () => {
  const sweepApi = useSweepApi();

  return {
    post_createObject: useCallback(
      async ({ payload }: { payload: { name: string; label: string; visible: boolean } }) => {
        const response = await sweepApi.post<ObjectData>(`/documentation/objects`, payload);
        return response.data;
      },
      [sweepApi],
    ),
    patch_toggleObjectVisibility: useCallback(
      async (objectName: string, visible: boolean) => {
        return await sweepApi.patch(`/documentation/objects/${objectName}`, {
          visible,
        });
      },
      [sweepApi],
    ),
    get_metadataTypes: useCallback(async (): Promise<BackendDocumentationMetadataTypesTab[]> => {
      const response = await sweepApi.get<BackendDocumentationMetadataTypesTab[]>(
        `/documentation/metadataTypes`,
      );
      return response.data;
    }, [sweepApi]),
    get_metadataData: useCallback(
      async (metadataTypeName: string) => {
        const response = await sweepApi.get<ConfigurationInstanceData[]>(
          `/documentation/metadataTypes/${metadataTypeName}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    get_metadataByMetadataId: useCallback(
      async (metadataId: string, crmOrgId: string) => {
        const response = await sweepApi.get<CardType>(
          `/documentation/${metadataId}?crmOrgId=${crmOrgId}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    post_metadata: useCallback(
      async (metadataTypeName: string) => {
        await sweepApi.post(`/documentation/metadataTypes`, { metadataTypeName });
      },
      [sweepApi],
    ),
    delete_metadataType: useCallback(
      async (metadataTypeName: string) => {
        await sweepApi.delete(`/documentation/metadataTypes/${metadataTypeName}`);
      },
      [sweepApi],
    ),
  };
};
